import {
  AlertTriangle,
  Download,
  RotateCcw,
  TrendingDown,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { StatBox } from "../../../components/ui/stat-box";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { baixarCsv, centavosParaCsv } from "../../../utils/csv";
import { useOpsMetrics } from "../hooks/use-ops-metrics";
import { OpsMetrics, OpsMetricsResult } from "../types";

const moeda = (centavos: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(centavos / 100);

const inteiro = (v: number) => new Intl.NumberFormat("pt-BR").format(v);

/** Razão em porcentagem; devolve null quando não há base para dividir */
const razao = (parte: number, total: number) => (total > 0 ? (parte / total) * 100 : null);

const percentual = (v: number | null) => (v === null ? "—" : `${v.toFixed(1)}%`);

/** No CSV o percentual vai como número (vírgula decimal, sem "%") para o Excel somar. */
const percentualCsv = (v: number | null) => (v === null ? "" : v.toFixed(1).replace(".", ","));

const COLUNAS_TABELA = [
  "Ops",
  "Ativos",
  "Cadastrados",
  "Novos",
  "Valor face",
  "Valor compra",
  "Deságio",
  "Deságio %",
  "Antecip. liq.",
  "Face médio",
  "Romaneio",
  "Adesão cadastro %",
  "Adesão R$ %",
  "Adesão antecip. %",
];

const linhaCsv = (l: OpsMetrics) => [
  l.ops,
  l.cadastrosAtivos,
  l.cadastrosTotal,
  l.cadastrosNovos,
  centavosParaCsv(l.valorFace),
  centavosParaCsv(l.valorCompra),
  centavosParaCsv(l.desagio),
  percentualCsv(razao(l.desagio, l.valorFace)),
  l.qtdAntecipacoes,
  centavosParaCsv(l.valorFaceMedio),
  centavosParaCsv(l.romaneioValor),
  percentualCsv(razao(l.romaneioPessoasComCadastro, l.romaneioPessoas)),
  percentualCsv(razao(l.romaneioValorComCadastro, l.romaneioValor)),
  percentualCsv(razao(l.romaneioPessoasComAntecipacao, l.romaneioPessoasComCadastro)),
];

/** Os mesmos seis números dos StatBox, em duas colunas. */
const linhasResumoCsv = (total: OpsMetrics) => [
  ["Métrica", "Valor"],
  ["Cadastrados ativos", total.cadastrosAtivos],
  ["Cadastrados no total", total.cadastrosTotal],
  ["Cadastros novos no mês", total.cadastrosNovos],
  ["Adiantado liquidado (face)", centavosParaCsv(total.valorFace)],
  ["Deságio total", centavosParaCsv(total.desagio)],
  ["Antecipações liquidadas", total.qtdAntecipacoes],
];

/** Mês parcial vira arquivo com sufixo próprio, para não se passar por fechamento. */
const nomeArquivo = (periodo: OpsMetricsResult["periodo"], sufixo = "") =>
  `metricas-ops${sufixo}-${periodo.mes}${periodo.aberto ? "-parcial" : ""}.csv`;

/** Verde acima de 60%, âmbar entre 30 e 60, vermelho abaixo — para leitura rápida da adesão */
function AdesaoBadge({ valor }: { valor: number | null }) {
  if (valor === null) return <span className="text-muted-foreground">—</span>;
  const tom =
    valor >= 60
      ? "bg-green-100 text-green-800 hover:bg-green-100"
      : valor >= 30
        ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
        : "bg-red-100 text-red-800 hover:bg-red-100";
  return <Badge className={`${tom} font-medium`}>{percentual(valor)}</Badge>;
}

export default function OpsMetricsPage() {
  const { meses, mes, setMes, metrics, isLoading, isFetching, error, refetch } = useOpsMetrics();

  const total = metrics?.total;

  const linhas: OpsMetrics[] = metrics?.ops ?? [];
  const semDados = !metrics || linhas.length === 0;

  const baixarTabela = () => {
    if (!metrics) return;
    baixarCsv([COLUNAS_TABELA, ...linhas.map(linhaCsv)], nomeArquivo(metrics.periodo));
  };

  const baixarResumo = () => {
    if (!metrics?.total) return;
    baixarCsv(linhasResumoCsv(metrics.total), nomeArquivo(metrics.periodo, "-resumo"));
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Métricas das Operações</h1>
          <p className="text-muted-foreground mt-1">
            {metrics?.periodo.aberto
              ? "Resumo por ops no mês em aberto"
              : "Resumo por ops no mês fechado"}
            {metrics && ` — ${metrics.periodo.inicio} a ${metrics.periodo.fim}`}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={mes ?? ""} onValueChange={setMes} disabled={meses.length === 0}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Carregando meses…" />
            </SelectTrigger>
            <SelectContent>
              {meses.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={baixarTabela} disabled={semDados}>
            <Download className="h-4 w-4" />
            CSV da tabela
          </Button>

          <Button variant="outline" onClick={baixarResumo} disabled={semDados}>
            <Download className="h-4 w-4" />
            CSV do resumo
          </Button>

          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RotateCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {metrics?.periodo.aberto && (
        <Card className="mb-6 border-amber-400 bg-amber-50">
          <CardContent className="flex items-start gap-3 py-4 text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <p className="text-sm">
              <strong>Mês em aberto.</strong> Os números cobrem de{" "}
              {metrics.periodo.inicio} a {metrics.periodo.fim} e ainda vão mudar até o
              fim do mês. Médias, deságio % e taxas de adesão são parciais e não devem ser
              comparados com os meses já fechados.
            </p>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="mb-6 border-red-400">
          <CardContent className="py-4 text-red-700">{error}</CardContent>
        </Card>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Apurando as métricas do mês...</p>
          </CardContent>
        </Card>
      ) : !metrics || linhas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Sem movimento no período
            </h2>
            <p className="text-muted-foreground">
              Nenhuma ops teve cadastro, romaneio ou antecipação neste mês.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {total && (
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
              <StatBox
                title="Cadastrados ativos"
                value={total.cadastrosAtivos}
                icon={<Users className="h-5 w-5" />}
              />
              <StatBox
                title="Cadastrados no total"
                value={total.cadastrosTotal}
                icon={<Users className="h-5 w-5" />}
              />
              <StatBox
                title="Cadastros novos no mês"
                value={total.cadastrosNovos}
                icon={<UserPlus className="h-5 w-5" />}
              />
              <StatBox
                title="Adiantado liquidado (face)"
                value={total.valorFace}
                valueType="cents"
                icon={<Wallet className="h-5 w-5" />}
              />
              <StatBox
                title="Deságio total"
                value={total.desagio}
                valueType="cents"
                icon={<TrendingDown className="h-5 w-5" />}
              />
              <StatBox
                title="Antecipações liquidadas"
                value={total.qtdAntecipacoes}
                icon={<Wallet className="h-5 w-5" />}
              />
            </div>
          )}

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="min-w-[92rem]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Ops</TableHead>
                    <TableHead className="w-28 text-right">Ativos</TableHead>
                    <TableHead className="w-28 text-right">Cadastrados</TableHead>
                    <TableHead className="w-24 text-right">Novos</TableHead>
                    <TableHead className="w-36 text-right">Valor face</TableHead>
                    <TableHead className="w-36 text-right">Valor compra</TableHead>
                    <TableHead className="w-32 text-right">Deságio</TableHead>
                    <TableHead className="w-24 text-right">Deságio %</TableHead>
                    <TableHead className="w-28 text-right">Antecip. liq.</TableHead>
                    <TableHead className="w-36 text-right">Face médio</TableHead>
                    <TableHead className="w-36 text-right">Romaneio</TableHead>
                    <TableHead className="w-32 text-center">Adesão cadastro</TableHead>
                    <TableHead className="w-32 text-center">Adesão R$</TableHead>
                    <TableHead className="w-32 text-center">Adesão antecip.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linhas.map((l) => (
                    <TableRow key={l.ops}>
                      <TableCell className="font-medium">{l.ops}</TableCell>
                      <TableCell className="text-right">{inteiro(l.cadastrosAtivos)}</TableCell>
                      <TableCell className="text-right">{inteiro(l.cadastrosTotal)}</TableCell>
                      <TableCell className="text-right">{inteiro(l.cadastrosNovos)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {moeda(l.valorFace)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {moeda(l.valorCompra)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {moeda(l.desagio)}
                      </TableCell>
                      <TableCell className="text-right">
                        {percentual(razao(l.desagio, l.valorFace))}
                      </TableCell>
                      <TableCell className="text-right">{inteiro(l.qtdAntecipacoes)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {moeda(l.valorFaceMedio)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {moeda(l.romaneioValor)}
                      </TableCell>
                      <TableCell className="text-center">
                        <AdesaoBadge
                          valor={razao(l.romaneioPessoasComCadastro, l.romaneioPessoas)}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <AdesaoBadge
                          valor={razao(l.romaneioValorComCadastro, l.romaneioValor)}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <AdesaoBadge
                          valor={razao(
                            l.romaneioPessoasComAntecipacao,
                            l.romaneioPessoasComCadastro
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          <div className="mt-4 space-y-1 text-xs text-muted-foreground">
            <p>
              <strong>Cadastrados</strong> conta pessoas distintas — a chave é o ifood_uuid quando
              existe e o número do documento quando não, já que um mesmo entregador tem várias
              linhas em doc_cadastro_wts.
            </p>
            <p>
              <strong>Adesão cadastro</strong> é a fatia de quem rodou no romaneio do mês que tem
              cadastro; entrego casa por ifood_uuid e muve por CPF/CNPJ. A coluna{" "}
              <strong>Adesão R$</strong> é a mesma conta ponderada pelo valor do romaneio, e{" "}
              <strong>Adesão antecip.</strong> é a fatia dos cadastrados que antecipou no mês.
            </p>
            <p>
              Toda métrica de antecipação conta apenas o que foi{" "}
              <strong>liquidado</strong> — solicitação aberta, congelada ou com pagamento falho
              fica de fora.
            </p>
            <p>
              O recorte do mês usa a data de romaneio, não a data do pedido, para que romaneio e
              antecipação caiam na mesma janela.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
